import { createContext, useCallback, useContext, useEffect, useId, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useNavigate } from "react-router";
import { CheckIcon, ChevronDownIcon, CircleAlertIcon, TriangleAlertIcon, XIcon } from "lucide-react";
import type { Meal } from "@/data/meal-plan";
import type { DayOfWeek, DayPlan } from "@/types/meal-plan-state";
import { DAYS_OF_WEEK } from "@/types/meal-plan-state";
import { cloneSourceForDuplication, useWeekPlan } from "@/context/week-plan-context";
import {
  chooseComidasMenu,
  comidasMode,
  dayLunchDinner,
  dayMealIndex,
  dinnerLikeLunch,
  mealMatch,
  noteAt,
  sameNote,
  saveComidasNote,
  type ComidasMeal,
  type ComidasTarget,
  type DayLunchDinner,
  type MealMatch,
} from "@/lib/comidas-day";
import {
  dayChipKind,
  friendMealStatus,
  noteChanges,
  type FriendMealStatus,
  type NoteChange,
} from "@/lib/comidas-share";
import { clearMeals, copyMealsFromDay } from "@/lib/random-plan";
import { getTodayDate, getTodayDayOfWeek, getWeekDates } from "@/lib/week-utils";
import { fetchMealNoteHistory, SELF_SCOPE } from "@/lib/api";
import {
  matchSuggestions,
  mergeNoteHistories,
  noteSuggestions,
  relativeDay,
  weekNoteHistory,
  withoutWeek,
  type NoteHistory,
  type NoteSuggestion,
} from "@/lib/note-suggestions";
import { ownerScope } from "@/lib/week-owner";
import { weekPath } from "@/lib/week-paths";
import { focusRing } from "@/lib/format";
import { cn } from "@/lib/utils";
import { MealCard } from "./meal-card";
import { DuplicateDayDialog } from "./duplicate-day-dialog";
import { CopyMealNotesSection } from "./copy-meal-notes-dialog";
import { FriendChip, ShareBar, useComidasShare, type ComidasShare, type ShareWriterView } from "./comidas-share";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { ComprasToast, useComprasToast } from "@/components/ui/compras-toast";
import { hasEatenMarks, withEatenLossWarning } from "@/lib/owner-eaten";

const DAY_NAMES: Record<DayOfWeek, string> = {
  mon: "lunes",
  tue: "martes",
  wed: "miércoles",
  thu: "jueves",
  fri: "viernes",
  sat: "sábado",
  sun: "domingo",
};

const UNDO_MS = 5000;

/** "david", "david y mateo", "david, mateo y ana". */
function joinNames(names: string[]): string {
  return names.length <= 1 ? (names[0] ?? "") : `${names.slice(0, -1).join(", ")} y ${names.at(-1)}`;
}

// Suggestion list for the note-editor autocomplete dropdown. Provided by
// ComidasPanel so individual NoteEditors don't each refetch.
const NoteSuggestionsContext = createContext<NoteSuggestion[]>([]);

interface ComidasActions {
  /**
   * Replace my day with `next` and share its lunch / dinner note changes
   * with the friends switched on. Returns the friends a meal was written to
   * (their week loaded; a loading week applies it when it arrives).
   */
  applyDay: (day: DayOfWeek, next: DayPlan) => string[];
  /** Share an action that isn't a change of my day ("Reemplazar", "Copiar"). */
  shareTo: (view: ShareWriterView, day: DayOfWeek, changes: NoteChange[], force?: boolean) => void;
  /** A toast with Deshacer that puts `before` back while the day is still `after`. */
  offerUndo: (message: string, day: DayOfWeek, before: DayPlan, after: DayPlan) => void;
  share: ComidasShare;
}

const ComidasActionsContext = createContext<ComidasActions | null>(null);

function useComidasActions(): ComidasActions {
  const ctx = useContext(ComidasActionsContext);
  if (!ctx) throw new Error("useComidasActions must be used within ComidasPanel");
  return ctx;
}

/**
 * Comidas: one compact row per day. A row's single input is the dish for
 * lunch and dinner ("same"); "Cena distinta" gives dinner its own input.
 * Each row says what the dish filled, loudly when it didn't. In my own weeks,
 * "También para" writes the same dishes into friends' weeks (their plans,
 * their portions; see `comidas-share.ts`).
 */
export function ComidasPanel() {
  const { state, dispatch, owner } = useWeekPlan();
  const isFriend = owner.kind === "friend";

  const weekId = state.currentWeekId;

  // Every note from the server with the days it was picked: the owner's, plus
  // in friend mode the planner's own, whose spelling wins (they're the
  // household's dishes). Refetched per week, so the stats include edits made
  // in the week left behind.
  const [ownerNotes, setOwnerNotes] = useState<NoteHistory[]>([]);
  const [plannerNotes, setPlannerNotes] = useState<NoteHistory[]>([]);
  useEffect(() => {
    let cancelled = false;
    // Autocomplete is a nice-to-have — silently degrade.
    fetchMealNoteHistory(ownerScope(owner))
      .then((notes) => {
        if (!cancelled) setOwnerNotes(notes);
      })
      .catch(() => {});
    if (isFriend) {
      fetchMealNoteHistory(SELF_SCOPE)
        .then((notes) => {
          if (!cancelled) setPlannerNotes(notes);
        })
        .catch(() => {});
    }
    return () => {
      cancelled = true;
    };
  }, [owner, isFriend, weekId]);

  // The shown week counts as on screen, not as last fetched, so a note just
  // typed is offered (and counted) in the next input without a refetch.
  const suggestions = useMemo(
    () =>
      noteSuggestions(
        mergeNoteHistories(weekNoteHistory(weekId, state.weekPlan), plannerNotes, withoutWeek(ownerNotes, weekId)),
      ),
    [ownerNotes, plannerNotes, state.weekPlan, weekId],
  );
  const share = useComidasShare(state.weekLoaded ? weekId : "", !isFriend);
  const { toast, show: showToast, dismiss: dismissToast } = useComprasToast();

  const weekPlanRef = useRef(state.weekPlan);
  weekPlanRef.current = state.weekPlan;
  const writersRef = useRef(share.writers);
  writersRef.current = share.writers;

  const applyDay = useCallback(
    (day: DayOfWeek, next: DayPlan) => {
      const before = weekPlanRef.current[day];
      if (next === before) return [];
      dispatch({ type: "SET_DAY_PLAN", day, plan: next });
      const changes = noteChanges(before, next);
      if (changes.length === 0) return [];
      const written: string[] = [];
      for (const view of writersRef.current) {
        const outcomes = view.writer.apply({ day, changes });
        if (outcomes && Object.values(outcomes).some((o) => o?.kind === "written")) written.push(view.friend.username);
      }
      return written;
    },
    [dispatch],
  );

  const shareTo = useCallback(
    (view: ShareWriterView, day: DayOfWeek, changes: NoteChange[], force?: boolean) => {
      if (changes.length > 0) view.writer.apply({ day, changes, force });
    },
    [],
  );

  const offerUndo = useCallback(
    (message: string, day: DayOfWeek, before: DayPlan, after: DayPlan) => {
      showToast({
        message,
        tone: "success",
        actionLabel: "Deshacer",
        durationMs: UNDO_MS,
        onAction: () => {
          if (weekPlanRef.current[day] === after) applyDay(day, before);
        },
      });
    },
    [showToast, applyDay],
  );

  const actions = useMemo(() => ({ applyDay, shareTo, offerUndo, share }), [applyDay, shareTo, offerUndo, share]);

  // Say once what switching a friend on does.
  const toggleWithHint = useCallback(
    (friendId: number) => {
      const friend = share.friends.find((f) => f.friendId === friendId);
      const wasOn = share.isOn(friendId);
      share.toggle(friendId);
      if (!friend) return;
      showToast({
        message: wasOn
          ? `Ya no se guardará en la semana de ${friend.username}. Lo que ya le pusiste se queda.`
          : `Lo que escribas también se guarda en la semana de ${friend.username}, con las porciones de su plan. Si ya tiene otra comida, no la cambiamos.`,
        tone: "info",
        durationMs: 6000,
      });
    },
    [share, showToast],
  );

  const todayDay = getTodayDayOfWeek(weekId);
  const dates = weekId ? getWeekDates(weekId) : [];

  return (
    <NoteSuggestionsContext.Provider value={suggestions}>
      <ComidasActionsContext.Provider value={actions}>
        <div className="space-y-3">
          {/* Friend mode: copy my lunch and dinner notes into the friend's week. */}
          {isFriend && <CopyMealNotesSection key={weekId} />}
          {!isFriend && (
            <ShareBar
              share={{ ...share, toggle: toggleWithHint }}
              myWeek={state.weekPlan}
              onCopied={(message) => showToast({ message, tone: "success" })}
            />
          )}
          <OpenFriendProvider key={weekId}>
            <div className="divide-y rounded-lg border bg-card">
              {DAYS_OF_WEEK.map(({ id, label }) => (
                <DayRow
                  key={`${weekId}-${id}`}
                  day={id}
                  shortLabel={label}
                  dayNum={dates.find((d) => d.day === id)?.dayNum ?? null}
                  isToday={id === todayDay}
                />
              ))}
            </div>
          </OpenFriendProvider>
        </div>
        <ComprasToast toast={toast} onDismiss={dismissToast} />
      </ComidasActionsContext.Provider>
    </NoteSuggestionsContext.Provider>
  );
}

// ── One day ──

const mealOf = (day: DayPlan, index: number, meal: Meal): MealMatch =>
  mealMatch(noteAt(day, index), meal, day.mealMenus?.[index], day.meals?.[index] ?? []);

function DayRow({
  day,
  shortLabel,
  dayNum,
  isToday,
}: {
  day: DayOfWeek;
  shortLabel: string;
  dayNum: number | null;
  isToday: boolean;
}) {
  const { state, dispatch, owner, basePath } = useWeekPlan();
  const { applyDay, offerUndo, share } = useComidasActions();
  const dayPlan = state.weekPlan[day];
  const ld = dayLunchDinner(dayPlan);
  const [expanded, setExpanded] = useState(false);
  const [splitOpen, setSplitOpen] = useState(false);
  const [focusDinner, setFocusDinner] = useState(false);
  /** Replacing meals with "comido" ticks (emptying them, "Igual que…") asks first. */
  const [pendingConfirm, setPendingConfirm] = useState<{
    next: DayPlan;
    undo: (written: string[]) => string;
    title: string;
  } | null>(null);
  const [showDup, setShowDup] = useState(false);
  const [confirmClearDay, setConfirmClearDay] = useState(false);
  const dinnerInputRef = useRef<HTMLInputElement>(null);
  const titleId = useId();
  const dayName = DAY_NAMES[day];
  const dayTitle = `${dayName}${dayNum ? ` ${dayNum}` : ""}`;

  useEffect(() => {
    if (!focusDinner) return;
    setFocusDinner(false);
    dinnerInputRef.current?.focus();
    dinnerInputRef.current?.select();
  }, [focusDinner]);

  const dayColumn = (
    <div className="w-9 shrink-0 pt-1.5 text-center leading-tight">
      <div
        id={titleId}
        className={cn(
          "text-sm font-semibold",
          isToday && "underline decoration-primary decoration-2 underline-offset-4",
        )}
        aria-current={isToday ? "date" : undefined}
      >
        {shortLabel}
      </div>
      {dayNum !== null && <div className="text-xs text-muted-foreground">{dayNum}</div>}
    </div>
  );

  if (!ld) {
    const regimenMissing = !dayPlan.regimenId;
    return (
      <div className="flex items-center gap-2 px-3 py-2" role="group" aria-labelledby={titleId}>
        {dayColumn}
        <p className="flex-1 text-sm text-muted-foreground">
          {regimenMissing ? (
            <Link
              to={`${basePath}/${state.currentWeekId}/${day}`}
              className={cn("font-medium text-primary underline underline-offset-2", focusRing)}
            >
              Elige el tipo de día
            </Link>
          ) : (
            "Este tipo de día no tiene almuerzo y cena"
          )}
        </p>
      </div>
    );
  }

  const mode = comidasMode(dayPlan, ld);
  const distinct = mode === "distinct" || splitOpen;
  const lunchNote = noteAt(dayPlan, ld.lunchIdx);
  const dinnerNote = noteAt(dayPlan, ld.dinnerIdx);
  const sharing = share.writers.length > 0;
  const sharedNames = joinNames(share.writers.map((w) => w.friend.username));

  function indicesOf(target: ComidasTarget): number[] {
    return target === "both" ? [ld!.lunchIdx, ld!.dinnerIdx] : [target === "lunch" ? ld!.lunchIdx : ld!.dinnerIdx];
  }

  function clearMessage(target: ComidasTarget, written: string[]): string {
    const what = target === "both" ? `el ${dayName}` : `${target === "lunch" ? "el almuerzo" : "la cena"} del ${dayName}`;
    return written.length > 0 ? `Borraste ${what}, también para ${joinNames(written)}.` : `Borraste ${what}.`;
  }

  /** Commit `next`; with `undo`, offer Deshacer (its message may name who else it reached). */
  function commit(next: DayPlan, undo?: (written: string[]) => string) {
    const before = dayPlan;
    const written = applyDay(day, next);
    if (undo) offerUndo(undo(written), day, before, next);
  }

  function save(target: ComidasTarget, note: string) {
    const next = saveComidasNote(dayPlan, target, note);
    if (note.trim()) return commit(next);
    // Emptying the text clears the meal; my own "comido" ticks ask first.
    const hadContent = indicesOf(target).some((i) => noteAt(dayPlan, i) || dayPlan.mealMenus?.[i] !== undefined);
    if (!hadContent) return commit(next);
    if (hasEatenMarks(dayPlan.meals, indicesOf(target))) {
      setPendingConfirm({ next, undo: (written) => clearMessage(target, written), title: "¿Borrar esta comida?" });
      return;
    }
    commit(next, (written) => clearMessage(target, written));
  }

  /** Foods picked by hand with no note on either meal: there's no dish to make both meals share. */
  const unifiable = mode === "same" || lunchNote !== "" || dinnerNote !== "";

  function unify() {
    setSplitOpen(false);
    if (mode === "same" || !unifiable) return;
    const next = lunchNote ? dinnerLikeLunch(dayPlan) : saveComidasNote(dayPlan, "lunch", dinnerNote);
    const message = lunchNote
      ? `La cena del ${dayName} ahora es igual al almuerzo.`
      : `El almuerzo del ${dayName} ahora es igual a la cena.`;
    if (hasEatenMarks(dayPlan.meals, [lunchNote ? ld!.dinnerIdx : ld!.lunchIdx])) {
      setPendingConfirm({ next, undo: () => message, title: lunchNote ? "¿Cambiar la cena?" : "¿Cambiar el almuerzo?" });
      return;
    }
    commit(next, () => message);
  }

  function split() {
    setSplitOpen(true);
    setFocusDinner(true);
  }

  const chooseMenu = (target: ComidasTarget, menuId: string) => commit(chooseComidasMenu(dayPlan, target, menuId));
  const expand = () => setExpanded(true);

  /** My status for `shown` (the saved day, or the preview of a draft): one line per meal unless they agree. */
  function myLines(shown: DayPlan, target: ComidasTarget): StatusLineSpec[] {
    const lunch = mealOf(shown, ld!.lunchIdx, ld!.lunch);
    const dinner = mealOf(shown, ld!.dinnerIdx, ld!.dinner);
    if (target === "lunch") return [{ match: lunch, target: "lunch" }];
    if (target === "dinner") return [{ match: dinner, target: "dinner" }];
    if (lunch.status === dinner.status && lunch.foods === dinner.foods) return [{ match: lunch, target: "both" }];
    return [
      { match: lunch, target: "lunch", label: "Alm." },
      { match: dinner, target: "dinner", label: "Cena" },
    ];
  }

  const friendMeals = (target: ComidasTarget): ComidasMeal[] =>
    target === "both" ? ["lunch", "dinner"] : [target];

  function renderStatus(target: ComidasTarget, draft: string | null, trailing: ReactNode) {
    const shown = draft === null ? dayPlan : saveComidasNote(dayPlan, target, draft);
    const clearing = draft !== null && !draft.trim();
    const preview = draft !== null;
    const lines = clearing ? [] : myLines(shown, target);
    const line = (spec: StatusLineSpec, key: number) => (
      <StatusLine
        key={key}
        {...spec}
        preview={preview}
        onElegir={expand}
        onChooseMenu={(menuId) => chooseMenu(spec.target, menuId)}
      />
    );
    // A plain result shares its line with the chips and links (they wrap
    // below when it's long); alerts get the full width.
    const plain = lines.length === 1 && (lines[0]!.match.status === "ok" || lines[0]!.match.status === "empty");
    return (
      <>
        <div className="flex flex-wrap items-start gap-x-1.5">
          {clearing && <p className="text-xs italic text-muted-foreground">→ se borrará · sin guardar</p>}
          {plain && <div className="min-w-0 max-w-full">{line(lines[0]!, 0)}</div>}
          <div className="ml-auto flex shrink-0 items-center gap-1">{trailing}</div>
        </div>
        {!plain && lines.map(line)}
        {preview &&
          share.writers.slice(0, 2).map((view) => (
            <FriendPreviewLine key={view.friend.friendId} view={view} day={day} myBefore={dayPlan} myAfter={shown} meals={friendMeals(target)} />
          ))}
      </>
    );
  }

  const chips = (meals: ComidasMeal[]) =>
    share.writers.length > 0 ? <FriendChips day={day} myDay={dayPlan} meals={meals} /> : null;

  const linkButton = (label: string, onClick: () => void) => (
    <button
      type="button"
      onClick={onClick}
      className={cn("-my-1 shrink-0 rounded px-1 py-1 text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground", focusRing)}
    >
      {label}
    </button>
  );

  const ariaDay = `del ${dayTitle}`;
  // The first editor keeps its place (and its draft and focus) when the day
  // switches between one input and two.
  const editors = [
    <MealEditor
      key="first"
      label={distinct ? ld.lunch.label : undefined}
      saved={lunchNote}
      placeholder={distinct ? "Almuerzo" : "Almuerzo y cena"}
      ariaLabel={`${distinct ? "Almuerzo" : "Almuerzo y cena"} ${ariaDay}`}
      invalid={
        isInvalid(mealOf(dayPlan, ld.lunchIdx, ld.lunch)) ||
        (!distinct && isInvalid(mealOf(dayPlan, ld.dinnerIdx, ld.dinner)))
      }
      onSave={(note) => save(distinct ? "lunch" : "both", note)}
      renderStatus={(draft) =>
        distinct
          ? renderStatus("lunch", draft, chips(["lunch"]))
          : renderStatus(
              "both",
              draft,
              <>
                {chips(["lunch", "dinner"])}
                {lunchNote && linkButton("Cena distinta", split)}
              </>,
            )
      }
    />,
    distinct && (
      <MealEditor
        key="dinner"
        label={ld.dinner.label}
        inputRef={dinnerInputRef}
        saved={dinnerNote}
        placeholder="Cena"
        ariaLabel={`Cena ${ariaDay}`}
        invalid={isInvalid(mealOf(dayPlan, ld.dinnerIdx, ld.dinner))}
        onSave={(note) => save("dinner", note)}
        renderStatus={(draft) =>
          renderStatus(
            "dinner",
            draft,
            <>
              {chips(["dinner"])}
              {(lunchNote || dinnerNote || splitOpen) &&
                linkButton(lunchNote || !dinnerNote ? "Igual que el almuerzo" : "Igual que la cena", unify)}
            </>,
          )
        }
      />
    ),
  ];

  return (
    <section role="group" aria-labelledby={titleId}>
      <div className="flex gap-2 px-3 py-1.5">
        {dayColumn}
        <div className="min-w-0 flex-1 space-y-1.5">
          {editors}
          {share.writers.map((view) => (
            <FriendDayLine key={view.friend.friendId} view={view} day={day} myDay={dayPlan} />
          ))}
        </div>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          aria-label={`${expanded ? "Ocultar" : "Ver"} ingredientes del ${dayName}`}
          className={cn("-mr-1 grid size-9 shrink-0 place-items-center rounded-md text-muted-foreground hover:bg-muted", focusRing)}
        >
          <ChevronDownIcon className={cn("size-4 transition-transform", expanded && "rotate-180")} />
        </button>
      </div>

      {expanded && (
        <div className="space-y-3 px-3 pb-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <label className="flex min-h-9 cursor-pointer items-center gap-2 text-sm">
              <input
                type="checkbox"
                role="switch"
                checked={distinct}
                disabled={!unifiable}
                onChange={(e) => (e.target.checked ? split() : unify())}
                className="size-4 accent-primary"
              />
              Cena distinta al almuerzo
            </label>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setConfirmClearDay(true)}
                className="rounded-md px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-muted"
              >
                Limpiar
              </button>
              <button
                type="button"
                onClick={() => setShowDup(true)}
                className="rounded-md border px-2 py-1 text-xs transition-colors hover:bg-muted"
              >
                Duplicar
              </button>
            </div>
          </div>
          {([ld.lunchIdx, ld.dinnerIdx] as const).map((mealIndex) => (
            <MealCard
              key={mealIndex}
              meal={ld.regimen.meals[mealIndex]!}
              selections={dayPlan.meals![mealIndex]!}
              onSelectionChange={(cgi, s) =>
                dispatch({ type: "SET_SELECTION", day, mealIndex, choiceGroupIndex: cgi, selections: s })
              }
              menuId={dayPlan.mealMenus?.[mealIndex]}
              onMenuChange={(id) => dispatch({ type: "SET_MEAL_MENU", day, mealIndex, menuId: id })}
            />
          ))}
          {sharing && (
            <p className="text-xs text-muted-foreground">
              Los cambios de ingredientes son solo para ti. A {sharedNames} le llega el nombre de la comida.
            </p>
          )}
        </div>
      )}

      {showDup && dayPlan.regimenId && (
        <DuplicateDayDialog
          targetDay={day}
          targetRegimenId={dayPlan.regimenId}
          currentWeekId={state.currentWeekId}
          currentWeekPlan={state.weekPlan}
          weekList={state.weekList}
          title={`Duplicar a ${dayName}`}
          onDuplicate={(sourcePlan, mealIndices) => {
            commit(copyMealsFromDay(dayPlan, cloneSourceForDuplication(sourcePlan), mealIndices));
            setShowDup(false);
          }}
          onClose={() => setShowDup(false)}
        />
      )}

      <ConfirmDialog
        open={confirmClearDay}
        onOpenChange={setConfirmClearDay}
        title="¿Limpiar almuerzo y cena?"
        description={withEatenLossWarning(
          `Se eliminarán las selecciones de almuerzo, cena y sus notas.${sharing ? ` A ${sharedNames} también, si tiene la misma comida.` : ""}`,
          owner,
          hasEatenMarks(dayPlan.meals, [ld.lunchIdx, ld.dinnerIdx]),
        )}
        confirmText="Limpiar"
        destructive
        onConfirm={() => commit(clearMeals(dayPlan, ld.regimen, [ld.lunchIdx, ld.dinnerIdx]))}
      />

      <ConfirmDialog
        open={pendingConfirm !== null}
        onOpenChange={(open) => !open && setPendingConfirm(null)}
        title={pendingConfirm?.title ?? ""}
        description={
          owner.kind === "friend"
            ? `Se borrará lo que ${owner.username} marcó como comido.`
            : "Se perderá lo marcado como comido."
        }
        confirmText="Continuar"
        destructive
        onConfirm={() => {
          if (pendingConfirm) commit(pendingConfirm.next, pendingConfirm.undo);
          setPendingConfirm(null);
        }}
      />
    </section>
  );
}

const isInvalid = (m: MealMatch) => m.status === "none" || m.status === "ambiguous";

// ── Status lines ──

interface StatusLineSpec {
  match: MealMatch;
  /** The meal(s) a menu chip fills. */
  target: ComidasTarget;
  /** "Alm." / "Cena" when lunch and dinner differ. */
  label?: string;
}

const MISSING_TEXT = { carb: "el carbohidrato", protein: "la proteína" } as const;

/** "falta la proteína", "faltan el carbohidrato y la proteína". */
function missingText(match: MealMatch): string {
  const parts = match.missing.map((c) => MISSING_TEXT[c]);
  return `${parts.length > 1 ? "faltan" : "falta"} ${parts.join(" y ")}`;
}

/** "Cerdo no está en tu plan". */
function unknownText(match: MealMatch, whose: "tu" | "su"): string | null {
  if (match.unknown.length === 0) return null;
  return `${joinNames(match.unknown)} ${match.unknown.length > 1 ? "no están" : "no está"} en ${whose} plan`;
}

function StatusLine({
  match,
  label,
  preview,
  onElegir,
  onChooseMenu,
}: StatusLineSpec & { preview: boolean; onElegir: () => void; onChooseMenu: (menuId: string) => void }) {
  const prefix = label ? <span className="font-medium text-muted-foreground">{label} </span> : null;
  const arrow = preview ? <span className="text-muted-foreground">→ </span> : null;
  const unsaved = preview ? <span className="text-muted-foreground"> · sin guardar</span> : null;

  if (match.status === "ok" || match.status === "empty") {
    if (!match.foods) return preview ? <p className="text-xs italic text-muted-foreground">→ (vacía)</p> : null;
    return (
      <p className={cn("break-words text-xs", preview ? "italic text-primary/80" : "text-foreground/80")}>
        {prefix}
        {arrow}
        {match.foods}
        {unsaved}
      </p>
    );
  }

  const action = (text: string, onClick: () => void) =>
    preview ? null : (
      <button
        type="button"
        onClick={onClick}
        className={cn("font-medium underline underline-offset-2", focusRing)}
      >
        {text}
      </button>
    );

  if (match.status === "partial") {
    const unknown = unknownText(match, "tu");
    return (
      <p className="flex items-start gap-1 text-xs text-amber-800 dark:text-amber-300">
        <TriangleAlertIcon className="mt-px size-3.5 shrink-0" aria-hidden="true" />
        <span className={cn("min-w-0", preview && "italic")}>
          {prefix}
          {arrow}
          {[match.foods, missingText(match), unknown].filter(Boolean).join(" · ")}
          {preview ? unsaved : <> · {action("Elegir", onElegir)}</>}
        </span>
      </p>
    );
  }

  if (match.status === "ambiguous") {
    return (
      <div className="flex flex-wrap items-center gap-1 text-xs text-destructive">
        <CircleAlertIcon className="size-3.5 shrink-0" aria-hidden="true" />
        <span className={cn(preview && "italic")}>
          {prefix}
          {arrow}¿Qué opción es?
        </span>
        {preview
          ? unsaved
          : match.candidates.map((menu) => (
              <button
                key={menu.id}
                type="button"
                onClick={() => onChooseMenu(menu.id)}
                aria-label={`${menu.name}: ${menu.summary}`}
                title={menu.summary}
                className={cn(
                  "rounded-full border border-destructive/40 px-2 py-0.5 font-medium hover:bg-destructive/10",
                  focusRing,
                )}
              >
                {menu.name}
              </button>
            ))}
      </div>
    );
  }

  // none
  const unknown = unknownText(match, "tu");
  return (
    <p className="flex items-start gap-1 text-xs font-medium text-destructive">
      <CircleAlertIcon className="mt-px size-3.5 shrink-0" aria-hidden="true" />
      <span className={cn("min-w-0", preview && "italic")}>
        {prefix}
        {arrow}
        {["No reconocimos esta comida", unknown].filter(Boolean).join(" · ")}
        {preview ? unsaved : <> · {action("Elegir", onElegir)}</>}
      </span>
    </p>
  );
}

// ── Friends, per row ──

function statusesOf(view: ShareWriterView, day: DayOfWeek, myDay: DayPlan, meals: ComidasMeal[]): FriendMealStatus[] {
  const plan = view.state.plan;
  if (!plan) return [];
  return meals.map((meal) => friendMealStatus(myDay, plan[day], meal));
}

function FriendChips({ day, myDay, meals }: { day: DayOfWeek; myDay: DayPlan; meals: ComidasMeal[] }) {
  const { share } = useComidasActions();
  const [open, setOpen] = useContext(OpenFriendContext);
  return (
    <>
      {share.writers.map((view) => {
        const kind = dayChipKind(statusesOf(view, day, myDay, meals));
        if (!kind) return null;
        const saving = view.state.pending && view.state.lastDay === day;
        const id = `${view.friend.friendId}:${day}`;
        return (
          <FriendChip
            key={view.friend.friendId}
            username={view.friend.username}
            kind={kind}
            saving={saving}
            expanded={open.has(id)}
            onToggle={() =>
              setOpen((prev) => {
                const next = new Set(prev);
                if (next.has(id)) next.delete(id);
                else next.add(id);
                return next;
              })
            }
          />
        );
      })}
    </>
  );
}

/** Which "friend:day" detail lines were opened from their chip. */
const OpenFriendContext = createContext<[Set<string>, React.Dispatch<React.SetStateAction<Set<string>>>]>([
  new Set(),
  () => {},
]);

function OpenFriendProvider({ children }: { children: ReactNode }) {
  const state = useState<Set<string>>(() => new Set());
  return <OpenFriendContext.Provider value={state}>{children}</OpenFriendContext.Provider>;
}

const MEAL_WORD: Record<ComidasMeal, string> = { lunch: "el almuerzo", dinner: "la cena" };
const MEAL_NOUN: Record<ComidasMeal, string> = { lunch: "almuerzo", dinner: "cena" };

/**
 * Under a row, per friend: shown on its own when their copy of the dish has
 * a problem in their plan or my last write was kept from a meal of theirs;
 * otherwise only when opened from the chip.
 */
function FriendDayLine({ view, day, myDay }: { view: ShareWriterView; day: DayOfWeek; myDay: DayPlan }) {
  const { shareTo } = useComidasActions();
  const { state } = useWeekPlan();
  const navigate = useNavigate();
  const [open] = useContext(OpenFriendContext);
  const [confirmReplace, setConfirmReplace] = useState(false);
  const plan = view.state.plan;
  if (!plan) return null;
  const theirDay = plan[day];
  const name = view.friend.username;
  const meals: ComidasMeal[] = ["lunch", "dinner"];
  const statuses = meals.map((meal) => ({ meal, status: friendMealStatus(myDay, theirDay, meal) }));
  const toggled = open.has(`${view.friend.friendId}:${day}`);

  // A warning from my last write there, while their meal still isn't mine
  // (a refetch may show they took the dish since).
  const moment = view.state.outcomes[day] ?? null;
  const kept = moment
    ? meals.filter((m) => moment[m]?.kind === "kept" && statuses.find((s) => s.meal === m)!.status.kind !== "synced")
    : [];

  const myNote = (meal: ComidasMeal) => {
    const i = dayMealIndex(myDay, meal);
    return i === null ? "" : noteAt(myDay, i);
  };
  /** Their kept or different meals, set to my dish (or cleared, when I have none). */
  const replaceChanges = (): NoteChange[] =>
    meals.flatMap((meal) => {
      if (statuses.find((s) => s.meal === meal)!.status.kind !== "different" && !kept.includes(meal)) return [];
      const i = dayMealIndex(theirDay, meal);
      if (i === null) return [];
      const theirs = noteAt(theirDay, i);
      const next = myNote(meal);
      return next !== "" && sameNote(next, theirs) ? [] : [{ meal, prev: theirs, next }];
    });
  const replace = () => {
    const changes = replaceChanges();
    const indices = changes.map((c) => dayMealIndex(theirDay, c.meal)).filter((i): i is number => i !== null);
    if (hasEatenMarks(theirDay.meals, indices)) setConfirmReplace(true);
    else shareTo(view, day, changes, true);
  };
  // Save what's pending for them first, so their day opens with it.
  const openTheirDay = () =>
    void view.writer
      .flush()
      .catch(() => {})
      .then(() => navigate(weekPath(view.friend.friendId, state.currentWeekId, day)));

  const button = (text: string, onClick: () => void) => (
    <button type="button" onClick={onClick} className={cn("font-medium underline underline-offset-2", focusRing)}>
      {text}
    </button>
  );

  let tone: "warn" | "error" | "muted" = "muted";
  let content: ReactNode = null;

  if (kept.length > 0) {
    const outcome = moment![kept[0]!]!;
    const what = kept.length > 1 ? `el ${DAY_NAMES[day]}` : `${MEAL_WORD[kept[0]!]} del ${DAY_NAMES[day]}`;
    const reason =
      outcome.kind === "kept" && outcome.reason === "eaten"
        ? `ya marcó lo que comió`
        : outcome.kind === "kept" && outcome.reason === "picks"
          ? "eligió ingredientes a mano"
          : `tiene «${outcome.kind === "kept" ? outcome.note : ""}»`;
    tone = "warn";
    content = (
      <>
        No cambiamos {what} de {name}: {reason} · {button("Reemplazar", replace)} ·{" "}
        {button("Dejar", () => view.writer.dismiss(day))}
      </>
    );
  } else {
    // The most important meal decides the line; name the meal when only one has it.
    const ranked = statuses
      .map((s) => ({ ...s, kind: dayChipKind([s.status]) }))
      .filter((s) => s.kind !== null)
      .sort((a, b) => RANK.indexOf(a.kind!) - RANK.indexOf(b.kind!));
    const top = ranked[0];
    if (!top) return null;
    const both = ranked.length > 1 && ranked[1]!.kind === top.kind;
    const whichMeal = both ? "" : ` (${MEAL_NOUN[top.meal]})`;
    const s = top.status;
    const auto = top.kind === "warn" || top.kind === "error";
    if (!auto && !toggled) return null;
    if (s.kind === "synced") {
      if (s.match.status === "partial") {
        tone = "warn";
        content = (
          <>
            {name}
            {whichMeal}: {[s.match.foods, missingText(s.match), unknownText(s.match, "su")].filter(Boolean).join(" · ")} ·{" "}
            {button("Abrir", openTheirDay)}
          </>
        );
      } else if (s.match.status === "none" || s.match.status === "ambiguous") {
        tone = "error";
        content = (
          <>
            {name}
            {whichMeal}: {s.match.status === "none" ? "no reconocimos esta comida en su plan" : "¿qué opción es? Elígela en su día"}
            {s.match.status === "none" && unknownText(s.match, "su") ? ` · ${unknownText(s.match, "su")}` : ""} ·{" "}
            {button("Abrir", openTheirDay)}
          </>
        );
      } else {
        content = (
          <>
            {name}
            {whichMeal}: {s.match.foods || "guardado"} · {button("Abrir", openTheirDay)}
          </>
        );
      }
    } else if (s.kind === "different") {
      content = (
        <>
          {name}
          {whichMeal} {s.note ? `tiene «${s.note}»` : "eligió ingredientes a mano"} · {button("Reemplazar", replace)} ·{" "}
          {button("Abrir", openTheirDay)}
        </>
      );
    } else if (s.kind === "missing") {
      const copy = meals.flatMap((meal) =>
        statuses.find((x) => x.meal === meal)!.status.kind === "missing" ? [{ meal, prev: "", next: myNote(meal) }] : [],
      );
      content = (
        <>
          {name} no tiene esta comida{whichMeal} · {button("Copiar", () => shareTo(view, day, copy))}
        </>
      );
    } else if (s.kind === "no-day") {
      content = (
        <>
          {name} no tiene tipo de día este día · {button("Elegir", openTheirDay)}
        </>
      );
    } else if (s.kind === "no-meal") {
      content = (
        <>
          {name} no tiene {MEAL_NOUN[top.meal]} este día
        </>
      );
    }
  }

  if (!content) return null;
  const Icon = tone === "error" ? CircleAlertIcon : tone === "warn" ? TriangleAlertIcon : null;
  return (
    <>
      <p
        className={cn(
          "flex items-start gap-1 text-xs",
          tone === "error" && "text-destructive",
          tone === "warn" && "text-amber-800 dark:text-amber-300",
          tone === "muted" && "text-muted-foreground",
        )}
      >
        {Icon && <Icon className="mt-px size-3.5 shrink-0" aria-hidden="true" />}
        <span className="min-w-0">{content}</span>
      </p>
      <ConfirmDialog
        open={confirmReplace}
        onOpenChange={setConfirmReplace}
        title={`¿Reemplazar la comida de ${name}?`}
        description={`También se borrará lo que ${name} marcó como comido.`}
        confirmText="Reemplazar"
        destructive
        onConfirm={() => shareTo(view, day, replaceChanges(), true)}
      />
    </>
  );
}

const RANK = ["error", "warn", "different", "missing", "unavailable", "ok"] as const;

/** While typing: what the draft would do in a friend's week. */
function FriendPreviewLine({
  view,
  day,
  myBefore,
  myAfter,
  meals,
}: {
  view: ShareWriterView;
  day: DayOfWeek;
  myBefore: DayPlan;
  myAfter: DayPlan;
  meals: ComidasMeal[];
}) {
  const changes = noteChanges(myBefore, myAfter).filter((c) => meals.includes(c.meal));
  const result = changes.length > 0 ? view.writer.preview({ day, changes }) : null;
  if (!result) return null;
  const name = view.friend.username;
  const kept = changes.find((c) => result.outcomes[c.meal]?.kind === "kept");
  if (kept) {
    const outcome = result.outcomes[kept.meal]!;
    return (
      <p className="text-xs italic text-muted-foreground">
        {name}: no se cambia{outcome.kind === "kept" && outcome.note ? `, tiene «${outcome.note}»` : ""}
      </p>
    );
  }
  const statuses = changes.map((c) => friendMealStatus(myAfter, result.day, c.meal));
  const synced = statuses.find((s) => s.kind === "synced");
  if (!synced || synced.kind !== "synced") return null;
  const m = synced.match;
  const problem = m.status === "partial" ? missingText(m) : m.status === "none" ? "no reconocida en su plan" : m.status === "ambiguous" ? "¿qué opción?" : null;
  return (
    <p
      className={cn(
        "flex items-start gap-1 text-xs italic",
        !problem && "text-primary/80",
        m.status === "partial" && "text-amber-800 dark:text-amber-300",
        (m.status === "none" || m.status === "ambiguous") && "text-destructive",
      )}
    >
      {problem &&
        (m.status === "partial" ? (
          <TriangleAlertIcon className="mt-px size-3.5 shrink-0" aria-hidden="true" />
        ) : (
          <CircleAlertIcon className="mt-px size-3.5 shrink-0" aria-hidden="true" />
        ))}
      <span className="min-w-0">
        <span className="not-italic text-muted-foreground">{name} → </span>
        {problem ?? m.foods}
      </span>
    </p>
  );
}

// ── Note input with autocomplete ──

/** Enter moves on: the next day's (or dinner's) input, or closes the keyboard after the last. */
function focusNextInput(current: HTMLInputElement) {
  const inputs = Array.from(document.querySelectorAll<HTMLInputElement>("input[data-comidas-input]"));
  const next = inputs[inputs.indexOf(current) + 1];
  if (next) next.focus();
  else current.blur();
}

interface MealEditorProps {
  /** Small label above the input (distinct lunch / dinner). */
  label?: string;
  saved: string;
  onSave: (note: string) => void;
  placeholder: string;
  ariaLabel: string;
  /** Nothing matched: red border and aria-invalid. */
  invalid: boolean;
  /** Under the input: the saved result (`draft` null), or what saving the draft would do. */
  renderStatus: (draft: string | null) => ReactNode;
  inputRef?: React.Ref<HTMLInputElement>;
}

function MealEditor({ label, saved, onSave, placeholder, ariaLabel, invalid, renderStatus, inputRef }: MealEditorProps) {
  const [draft, setDraft] = useState(saved);
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(-1);
  const allSuggestions = useContext(NoteSuggestionsContext);
  const statusId = useId();
  const listRef = useRef<HTMLDivElement>(null);
  /** The highlight moved by arrow key (scroll it into view), not by hover. */
  const arrowedRef = useRef(false);

  useEffect(() => {
    setDraft(saved);
  }, [saved]);

  // The reducer saves the note trimmed, so whitespace alone isn't a change.
  const dirty = draft.trim() !== saved.trim();

  // Every past note, filtered by name or food once the user types (an
  // untouched saved note, opened with ArrowDown, shows them all).
  const filtered = useMemo(
    () => (open ? matchSuggestions(allSuggestions, dirty ? draft : "", saved) : []),
    [open, dirty, draft, saved, allSuggestions],
  );

  const showDropdown = open && filtered.length > 0;
  const today = getTodayDate();

  // Reset highlight when the visible list changes (a refetch or a sync reorders it).
  useEffect(() => {
    setHighlighted(-1);
  }, [draft, open, allSuggestions]);

  // Keep the arrowed-to option in view in a long list.
  useEffect(() => {
    if (!arrowedRef.current) return;
    arrowedRef.current = false;
    if (highlighted >= 0) listRef.current?.children[highlighted]?.scrollIntoView({ block: "nearest" });
  }, [highlighted]);

  function applySuggestion(value: string) {
    setDraft(value);
    setOpen(false);
    setHighlighted(-1);
    if (value !== saved) onSave(value);
  }

  function commitDraft() {
    setOpen(false);
    setHighlighted(-1);
    // Show the note as saved: the trimmed draft (`saved` may not change).
    setDraft(draft.trim());
    if (dirty) onSave(draft.trim());
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "ArrowDown" && !open) {
      e.preventDefault();
      setOpen(true);
      return;
    }
    if (e.key === "ArrowDown" && showDropdown) {
      e.preventDefault();
      arrowedRef.current = true;
      setHighlighted((i) => (i + 1) % filtered.length);
      return;
    }
    if (e.key === "ArrowUp" && showDropdown) {
      e.preventDefault();
      arrowedRef.current = true;
      setHighlighted((i) => (i <= 0 ? filtered.length - 1 : i - 1));
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      const input = e.currentTarget;
      const pick = showDropdown && highlighted >= 0 ? filtered[highlighted]?.suggestion.note : undefined;
      if (pick !== undefined) applySuggestion(pick);
      else commitDraft();
      focusNextInput(input);
      return;
    }
    if (e.key === "Escape") {
      if (open) {
        setOpen(false);
        setHighlighted(-1);
      } else {
        setDraft(saved);
      }
    }
  }

  return (
    <div className="space-y-0.5">
      {label && <div className="text-xs font-medium text-muted-foreground">{label}</div>}
      {/* The dropdown spans the whole row, so Guardar/Cancelar don't narrow it. */}
      <div className="relative">
        <div className="flex items-center gap-1">
          <div className="min-w-0 flex-1">
            <input
              ref={inputRef}
              type="text"
              data-comidas-input=""
              value={draft}
              onChange={(e) => {
                setDraft(e.target.value);
                setOpen(true);
              }}
              // Only an empty input opens the list on focus: over a filled day
              // it would cover the input below, and a tap meant for that
              // input would pick a dish here instead.
              onFocus={() => setOpen(draft.trim() === "")}
              onBlur={() => setOpen(false)}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              aria-label={ariaLabel}
              autoComplete="off"
              enterKeyHint="next"
              role="combobox"
              aria-expanded={showDropdown}
              aria-autocomplete="list"
              aria-invalid={!dirty && invalid ? true : undefined}
              aria-describedby={statusId}
              className={cn(
                "h-9 w-full rounded-md border bg-background px-2 text-base",
                !dirty && invalid && "border-destructive ring-1 ring-destructive/30",
              )}
            />
          </div>
          {dirty && (
            <>
              <button
                type="button"
                aria-label="Guardar"
                // Prevent input blur from racing with the click.
                onMouseDown={(e) => e.preventDefault()}
                onClick={commitDraft}
                className={cn("grid size-9 shrink-0 place-items-center rounded-md bg-primary text-primary-foreground", focusRing)}
              >
                <CheckIcon className="size-4" />
              </button>
              <button
                type="button"
                aria-label="Cancelar"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => setDraft(saved)}
                className={cn("grid size-9 shrink-0 place-items-center rounded-md border", focusRing)}
              >
                <XIcon className="size-4" />
              </button>
            </>
          )}
        </div>
        {showDropdown && (
          <div
            ref={listRef}
            role="listbox"
            className="absolute inset-x-0 top-full z-50 mt-1 max-h-72 overflow-y-auto overscroll-contain rounded-lg border bg-popover p-1 text-popover-foreground shadow-lg"
          >
            {filtered.map(({ suggestion, food }, i) => (
              <SuggestionOption
                key={suggestion.note}
                suggestion={suggestion}
                food={food}
                today={today}
                highlighted={i === highlighted}
                onHover={() => setHighlighted(i)}
                onPick={() => applySuggestion(suggestion.note)}
              />
            ))}
          </div>
        )}
      </div>
      <div id={statusId}>
        {renderStatus(dirty ? draft : null)}
      </div>
    </div>
  );
}

const DATE_FORMAT = new Intl.DateTimeFormat("es", { weekday: "short", day: "numeric", month: "short", year: "numeric" });

/**
 * One autocomplete option: the note (and under it the food it matched by,
 * when not its name), then on how many days it was picked and when last.
 */
function SuggestionOption({
  suggestion: { note, count, lastDate },
  food,
  today,
  highlighted,
  onHover,
  onPick,
}: {
  suggestion: NoteSuggestion;
  food: string | null;
  today: string;
  highlighted: boolean;
  onHover: () => void;
  onPick: () => void;
}) {
  return (
    <button
      type="button"
      role="option"
      aria-selected={highlighted}
      // Prevent input blur from firing before our click handler.
      onMouseDown={(e) => e.preventDefault()}
      onMouseEnter={onHover}
      onClick={onPick}
      className={cn(
        "flex w-full items-center gap-3 rounded-md px-2 py-1.5 text-left text-sm transition-colors hover:bg-muted",
        highlighted && "bg-muted",
      )}
    >
      <span className="min-w-0 flex-1 break-words">
        {note}
        {food && <span className="block text-xs text-muted-foreground">{food}</span>}
      </span>
      {lastDate && (
        <span
          className="flex shrink-0 flex-col items-end text-[11px] leading-tight tabular-nums whitespace-nowrap"
          title={`${count} ${count === 1 ? "vez" : "veces"} · última: ${DATE_FORMAT.format(new Date(`${lastDate}T00:00:00`))}`}
        >
          <span className="font-medium text-foreground/70">{count}×</span>
          <span className="text-muted-foreground">{relativeDay(lastDate, today)}</span>
        </span>
      )}
    </button>
  );
}
